import { useState } from 'react'
import { useAcceptNda } from '../api/rooms'
import type { RoomResponse } from '../api/types'
import { errorMessage, formatUtc } from '../lib/format'

/**
 * The NDA a participant must accept before the room shows them anything. The server
 * decides: while `nda.required && !nda.accepted` every permission the room reports is
 * false, so this screen is the whole room. Accepting names the version that was read,
 * and the server refuses anything but the current one.
 */
export function NdaGate({ room }: { room: RoomResponse }) {
  const accept = useAcceptNda(room.id)
  const [agreed, setAgreed] = useState(false)
  const nda = room.nda

  return (
    <section className="panel nda-gate" aria-labelledby="nda-title">
      <p className="small muted">
        Before you can see anything in this room, {room.organizationName} asks you to accept its
        terms{nda.setUtc ? ` (set ${formatUtc(nda.setUtc)})` : ''}.
      </p>
      <h2 id="nda-title" className="h2">
        {nda.title}
      </h2>
      <div className="nda-body" tabIndex={0}>
        {nda.body}
      </div>
      <form
        className="stack-sm"
        onSubmit={(e) => {
          e.preventDefault()
          if (agreed && nda.versionId) accept.mutate(nda.versionId)
        }}
      >
        <label className="check">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={accept.isPending}
          />
          <span>I have read these terms and accept them.</span>
        </label>
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!agreed || accept.isPending || !nda.versionId}
          >
            Accept and enter the room
          </button>
          {accept.isError && <span className="error-text small">{errorMessage(accept.error)}</span>}
        </div>
        <p className="small muted">
          Your acceptance is recorded with the time and the network address it came from.
        </p>
      </form>
    </section>
  )
}
